import { resolveUrl } from '@/lib/api-utils';
import { useEffect, useRef, useState } from 'react';
import { previewCache } from './docs.preview-cache';

// Display caps at 500 CSS px (see DocsDetailsPanel); multiply by device
// pixel ratio so hi-DPI screens get a sharp render. Falls back to 1 in
// non-DOM contexts (tests/SSR).
const MAX_CSS_PX = 500;

export type FilePreviewState =
  | { status: 'idle' }
  | { status: 'busy'; task: string }
  | { status: 'ready'; src: string }
  | { status: 'unsupported' }
  | { status: 'failed'; error: string };

interface Args {
  fileId: number | null;
  checksum: string | null;
  /**
   * File extension (with or without leading dot). Used to decide whether
   * the file is a previewable PDF.
   */
  extension: string | null;
  /** When true, bypass the cache and fetch a fresh copy of the source PDF. */
  punch?: boolean;
}

/**
 * Generate (or retrieve from cache) a thumbnail of the first PDF page for a
 * docs file. Returns a state machine the panel renders directly.
 *
 * - Non-PDF mime → 'unsupported'.
 * - Cached preview → 'ready' immediately (no network).
 * - Otherwise: fetch /api/files/:id, render page 1 to canvas (capped at
 *   500x500), encode as PNG Blob, cache under checksum, return an object
 *   URL. URL is revoked on unmount or when the file changes.
 */
export function useFilePreview({
  fileId,
  checksum,
  extension,
  punch = false,
}: Args): FilePreviewState {
  const [state, setState] = useState<FilePreviewState>({ status: 'idle' });
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Holds the in-flight pdfjs loading task so cleanup can terminate the
    // worker when the user navigates away mid-render.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let activeLoadingTask: { destroy: () => Promise<void> } | null = null;
    const revokeAndReplace = (next: FilePreviewState) => {
      if (cancelled) return;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (next.status === 'ready') {
        objectUrlRef.current = next.src;
      }
      setState(next);
    };

    if (fileId === null || checksum === null || extension === null) {
      revokeAndReplace({ status: 'idle' });
      return;
    }
    const normalisedExt = extension.replace(/^\./, '').toLowerCase();
    if (normalisedExt !== 'pdf') {
      revokeAndReplace({ status: 'unsupported' });
      return;
    }

    (async () => {
      try {
        // Cache hit (skip when punch requested).
        if (!punch) {
          const cached = await previewCache.get(checksum);
          if (!cancelled && cached) {
            revokeAndReplace({
              status: 'ready',
              src: URL.createObjectURL(cached),
            });
            return;
          }
        }

        revokeAndReplace({ status: 'busy', task: 'Downloading file' });
        const url = resolveUrl(`/files/${fileId}${punch ? '?punch=true' : ''}`);
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) {
          throw new Error(`Download failed: ${res.status} ${res.statusText}`);
        }
        const arrayBuffer = await res.arrayBuffer();
        if (cancelled) return;

        revokeAndReplace({ status: 'busy', task: 'Rendering preview' });
        const blob = await renderPdfThumbnail(arrayBuffer, (task) => {
          activeLoadingTask = task;
        });
        if (cancelled) return;

        // Cache write failures (e.g. quota exceeded) must not mask a
        // successful render — log and continue.
        try {
          await previewCache.put(checksum, blob);
        } catch (cacheErr) {
          // eslint-disable-next-line no-console
          console.warn('previewCache.put failed', cacheErr);
        }
        revokeAndReplace({ status: 'ready', src: URL.createObjectURL(blob) });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        revokeAndReplace({ status: 'failed', error: message });
      }
    })();

    return () => {
      cancelled = true;
      // Terminate any in-flight pdfjs worker so it doesn't keep rendering
      // a thumbnail the user can no longer see.
      activeLoadingTask?.destroy().catch(() => {});
      activeLoadingTask = null;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [fileId, checksum, extension, punch]);

  return state;
}

function installUpsertPolyfill() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proto = Map.prototype as any;
  if (typeof proto.getOrInsertComputed !== 'function') {
    proto.getOrInsertComputed = function <K, V>(
      this: Map<K, V>,
      key: K,
      callback: (key: K) => V,
    ): V {
      if (!this.has(key)) this.set(key, callback(key));
      return this.get(key) as V;
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wmProto = WeakMap.prototype as any;
  if (typeof wmProto.getOrInsertComputed !== 'function') {
    wmProto.getOrInsertComputed = function <K extends object, V>(
      this: WeakMap<K, V>,
      key: K,
      callback: (key: K) => V,
    ): V {
      if (!this.has(key)) this.set(key, callback(key));
      return this.get(key) as V;
    };
  }
}

// TODO(docs): consolidate PDF stacks. Thumbnails here use pdfjs-dist while
// the Lightbox full viewer uses @embedpdf/pdfium (see PdfViewerHeadless).
// Shipping two PDF engines roughly doubles the PDF bundle weight and doubles
// the maintenance surface — render thumbnails through the shared pdfium
// engine (or move the viewer to pdfjs) and drop one dependency.
async function renderPdfThumbnail(
  pdfData: ArrayBuffer,
  onTask?: (task: { destroy: () => Promise<void> }) => void,
): Promise<Blob> {
  // pdfjs uses Map.prototype.getOrInsertComputed (TC39 Upsert proposal); not
  // yet in Chrome 143. Polyfill before importing.
  installUpsertPolyfill();

  // Dynamic import so the bundle only pays the pdfjs cost when a preview is
  // actually requested. Worker uses Vite's ?worker import so the chunk is
  // bundled as a proper Web Worker — no MIME-sensitive dynamic ESM import
  // from the main thread (which previously broke in prod when the .mjs
  // asset was served as octet-stream by nginx
  const pdfjs = await import('pdfjs-dist');
  const PdfWorker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?worker'))
    .default;
  pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();

  const loadingTask = pdfjs.getDocument({ data: pdfData });
  onTask?.(loadingTask);
  const pdf = await loadingTask.promise;
  try {
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const dpr =
      typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const maxPx = MAX_CSS_PX * dpr;
    const scale = Math.min(
      maxPx / baseViewport.width,
      maxPx / baseViewport.height,
      1,
    );
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to obtain canvas 2d context');

    await page.render({ canvasContext: context, viewport }).promise;

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        'image/png',
      );
    });
  } finally {
    pdf.destroy();
  }
}
